
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import tradingQuotationService from "../services/tradingQuotationService";

const styles = {
  page: {
    padding: "24px",
    background: "#f7f8fc",
    minHeight: "100%",
    fontFamily: "Inter, Arial, sans-serif",
    color: "#252b3b",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "16px",
    flexWrap: "wrap",
    marginBottom: "22px",
  },
  title: { margin: 0, fontSize: "25px", fontWeight: 700 },
  subtitle: { margin: "7px 0 0", color: "#747b8d", fontSize: "13px" },
  button: {
    border: 0,
    borderRadius: "8px",
    padding: "11px 17px",
    background: "#6254d9",
    color: "#fff",
    fontWeight: 600,
    cursor: "pointer",
  },
  card: {
    background: "#fff",
    border: "1px solid #e8eaf2",
    borderRadius: "12px",
    padding: "18px",
    marginBottom: "18px",
  },
  stats: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
    gap: "16px",
    marginBottom: "24px",
  },
  statLabel: {
    color: "#64748b",
    fontSize: "12px",
    fontWeight: 600,
    marginBottom: "12px",
    letterSpacing: "0.2px",
  },
  statValue: {
    fontSize: "30px",
    fontWeight: 750,
    lineHeight: 1.2,
    color: "#0f172a",
    fontVariantNumeric: "tabular-nums",
  },
  statCard: {
    background: "#ffffff",
    border: "1px solid #e2e8f0",
    borderRadius: "14px",
    padding: "20px",
    marginBottom: 0,
    boxShadow: "0 2px 8px rgba(15, 23, 42, 0.04)",
  },
  statAccentTotal: { borderTop: "3px solid #6366f1" },
  statAccentCurrent: { borderTop: "3px solid #0891b2" },
  statAccentPending: { borderTop: "3px solid #f59e0b" },
  statAccentApproved: { borderTop: "3px solid #16a34a" },
  filters: {
    display: "flex",
    gap: "12px",
    flexWrap: "wrap",
    marginBottom: "16px",
  },
  input: {
    minWidth: "220px",
    flex: 1,
    border: "1px solid #dfe2eb",
    borderRadius: "8px",
    padding: "11px 12px",
    fontSize: "13px",
    outline: "none",
    boxSizing: "border-box",
  },
  select: {
    minWidth: "170px",
    border: "1px solid #dfe2eb",
    borderRadius: "8px",
    padding: "10px 12px",
    background: "#fff",
    fontSize: "13px",
  },
  tableWrap: { width: "100%", overflowX: "auto" },
  table: { width: "100%", borderCollapse: "collapse", minWidth: "1100px" },
  th: {
    textAlign: "left",
    padding: "13px 12px",
    background: "#f7f8fc",
    color: "#70778a",
    fontSize: "11px",
    textTransform: "uppercase",
    letterSpacing: ".4px",
    borderBottom: "1px solid #e8eaf2",
    whiteSpace: "nowrap",
  },
  td: {
    padding: "14px 12px",
    fontSize: "13px",
    borderBottom: "1px solid #f0f1f6",
    whiteSpace: "nowrap",
  },
  link: {
    color: "#5548cb",
    fontWeight: 700,
    cursor: "pointer",
    border: 0,
    padding: 0,
    background: "transparent",
  },
  error: {
    padding: "12px 14px",
    background: "#fff0f0",
    color: "#b42318",
    borderRadius: "8px",
    fontSize: "13px",
    marginBottom: "16px",
  },
  muted: {
    color: "#858b9b",
    fontSize: "13px",
    textAlign: "center",
    padding: "32px 12px",
  },
  footer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "12px",
    flexWrap: "wrap",
    marginTop: "15px",
    color: "#747b8d",
    fontSize: "12px",
  },
};

const getRows = (response) => {
  const body = response?.data?.data ?? response?.data;
  if (Array.isArray(body)) return body;
  if (Array.isArray(body?.items)) return body.items;
  return [];
};

const normalizeStatus = (value) =>
  String(value || "DRAFT").trim().toUpperCase();

const formatStatus = (value) =>
  normalizeStatus(value).replace(/_/g, " ");

const formatDate = (value) => {
  if (!value) return "—";
  const raw = String(value).slice(0, 10);
  const parts = raw.split("-");
  return parts.length === 3 ? `${parts[2]}-${parts[1]}-${parts[0]}` : raw;
};

const formatAmount = (value, currency = "INR") => {
  const amount = Number(value ?? 0);
  if (!Number.isFinite(amount)) return "—";

  try {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: currency || "INR",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch {
    return amount.toFixed(2);
  }
};

const statusColors = {
  DRAFT: {
    background: "#efedff",
    color: "#5548cb",
  },
  PENDING_APPROVAL: {
    background: "#fff4d6",
    color: "#946200",
  },
  APPROVED: {
    background: "#dcfce7",
    color: "#166534",
  },
  REJECTED: {
    background: "#fee2e2",
    color: "#b42318",
  },
  APPROVED_BY_CUSTOMER: {
    background: "#d1fae5",
    color: "#065f46",
  },
  REJECTED_BY_CUSTOMER: {
    background: "#ffe4e6",
    color: "#9f1239",
  },
  SENT_TO_CUSTOMER: {
    background: "#dbeafe",
    color: "#1d4ed8",
  },
};

function StatusBadge({ status }) {
  const colors = statusColors[normalizeStatus(status)] || {
    background: "#f1f3f9",
    color: "#5b6172",
  };

  return (
    <span
      style={{
        display: "inline-block",
        borderRadius: "20px",
        padding: "5px 9px",
        fontSize: "11px",
        fontWeight: 700,
        whiteSpace: "nowrap",
        ...colors,
      }}
    >
      {formatStatus(status)}
    </span>
  );
}

export default function TradingQuotationListPage() {
  const navigate = useNavigate();

  const [quotations, setQuotations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [page, setPage] = useState(1);
  const pageSize = 10;

  const loadQuotations = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const response = await tradingQuotationService.getQuotations();
      setQuotations(getRows(response));
    } catch (err) {
      setQuotations([]);
      setError(
        err?.response?.data?.message ||
          err?.response?.data?.error ||
          err?.message ||
          "Unable to load Trading Quotations."
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadQuotations();
  }, [loadQuotations]);

  const statuses = useMemo(
    () =>
      [...new Set(quotations.map((row) => normalizeStatus(row.status_code)))].sort(),
    [quotations]
  );

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();

    return quotations.filter((row) => {
      const matchesSearch =
        !term ||
        String(row.quotation_no || "").toLowerCase().includes(term) ||
        String(row.enquiry_no || "").toLowerCase().includes(term) ||
        String(row.costing_no || "").toLowerCase().includes(term) ||
        String(row.enquiry_id ?? "").toLowerCase().includes(term) ||
        String(row.costing_id ?? "").toLowerCase().includes(term) ||
        String(row.subject || "").toLowerCase().includes(term) ||
        String(row.customer_reference || "").toLowerCase().includes(term);

      const matchesStatus =
        statusFilter === "ALL" ||
        normalizeStatus(row.status_code) === statusFilter;

      return matchesSearch && matchesStatus;
    });
  }, [quotations, search, statusFilter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const startIndex = (currentPage - 1) * pageSize;
  const visibleRows = filtered.slice(startIndex, startIndex + pageSize);

  const currentVersions = quotations.filter(
    (row) => row.is_current_version !== false
  ).length;

  const pendingApproval = quotations.filter(
    (row) => normalizeStatus(row.status_code) === "PENDING_APPROVAL"
  ).length;

  const approved = quotations.filter(
    (row) => normalizeStatus(row.status_code) === "APPROVED"
  ).length;

  const openQuotation = (row) => {
    if (row.id) navigate(`/trading/quotations/${row.id}`);
  };

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Trading Quotations</h1>
          <p style={styles.subtitle}>
            Prepare customer quotations, review versions, and track approvals.
          </p>
        </div>

        <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
          <button
            type="button"
            style={styles.button}
            onClick={() => navigate("/trading/costings")}
          >
            View Trading Costings
          </button>

          <button
            type="button"
            style={styles.button}
            onClick={() => navigate("/trading/quotations/create")}
          >
            Create Quotation
          </button>
        </div>
      </div>

      {error && (
        <div role="alert" style={styles.error}>
          {error}
          <button
            type="button"
            onClick={loadQuotations}
            style={{
              marginLeft: "12px",
              border: 0,
              background: "transparent",
              color: "#b42318",
              fontWeight: 700,
              cursor: "pointer",
            }}
          >
            Retry
          </button>
        </div>
      )}

      <div style={styles.stats}>
        <div style={{ ...styles.statCard, ...styles.statAccentTotal }}>
          <div style={styles.statLabel}>Total Quotations</div>
          <div style={styles.statValue}>{quotations.length}</div>
        </div>

        <div style={{ ...styles.statCard, ...styles.statAccentCurrent }}>
          <div style={styles.statLabel}>Current Versions</div>
          <div style={styles.statValue}>{currentVersions}</div>
        </div>

        <div style={{ ...styles.statCard, ...styles.statAccentPending }}>
          <div style={styles.statLabel}>Pending Approval</div>
          <div style={styles.statValue}>{pendingApproval}</div>
        </div>

        <div style={{ ...styles.statCard, ...styles.statAccentApproved }}>
          <div style={styles.statLabel}>Approved Quotations</div>
          <div style={styles.statValue}>{approved}</div>
        </div>
      </div>

      <div style={styles.card}>
        <div style={styles.filters}>
          <input
            type="text"
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setPage(1);
            }}
            placeholder="Search quotation no., enquiry, costing or subject..."
            style={styles.input}
            aria-label="Search quotations"
          />

          <select
            value={statusFilter}
            onChange={(event) => {
              setStatusFilter(event.target.value);
              setPage(1);
            }}
            style={styles.select}
            aria-label="Filter quotations by status"
          >
            <option value="ALL">All statuses</option>
            {statuses.map((status) => (
              <option key={status} value={status}>
                {formatStatus(status)}
              </option>
            ))}
          </select>

          <button
            type="button"
            style={{ ...styles.button, opacity: loading ? 0.65 : 1 }}
            onClick={loadQuotations}
            disabled={loading}
          >
            {loading ? "Refreshing..." : "Refresh"}
          </button>
        </div>

        <div style={styles.tableWrap}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Quotation No.</th>
                <th style={styles.th}>Costing No.</th>
                <th style={styles.th}>Enquiry No.</th>
                <th style={styles.th}>Quotation Date</th>
                <th style={styles.th}>Valid Until</th>
                <th style={styles.th}>Version</th>
                <th style={styles.th}>Status</th>
                <th style={styles.th}>Net Amount</th>
                <th style={styles.th}>Current Version</th>
                <th style={styles.th}>Action</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={10} style={styles.muted}>
                    Loading Trading Quotations...
                  </td>
                </tr>
              ) : visibleRows.length === 0 ? (
                <tr>
                  <td colSpan={10} style={styles.muted}>
                    {error
                      ? "Quotations could not be loaded."
                      : "No Trading Quotations found."}
                  </td>
                </tr>
              ) : (
                visibleRows.map((row) => (
                  <tr key={row.id}>
                    <td style={styles.td}>
                      <button
                        type="button"
                        style={styles.link}
                        onClick={() => openQuotation(row)}
                      >
                        {row.quotation_no || `QUOT-${row.id}`}
                      </button>
                    </td>

                    <td style={styles.td}>{row.costing_no || "—"}</td>
                    <td style={styles.td}>{row.enquiry_no || "—"}</td>
                    <td style={styles.td}>{formatDate(row.quotation_date)}</td>
                    <td style={styles.td}>{formatDate(row.valid_until)}</td>
                    <td style={styles.td}>{row.version_no ?? 1}</td>

                    <td style={styles.td}>
                      <StatusBadge status={row.status_code} />
                    </td>

                    <td style={styles.td}>
                      {formatAmount(row.net_amount, row.currency_code)}
                    </td>

                    <td style={styles.td}>
                      {row.is_current_version === false ? (
                        <span style={{ color: "#858b9b" }}>No</span>
                      ) : (
                        <span style={{ color: "#15803d", fontWeight: 700 }}>
                          Yes
                        </span>
                      )}
                    </td>

                    <td style={styles.td}>
                      <button
                        type="button"
                        style={styles.link}
                        onClick={() => openQuotation(row)}
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div style={styles.footer}>
          <span>
            Showing {filtered.length === 0 ? 0 : startIndex + 1}–
            {Math.min(startIndex + pageSize, filtered.length)} of{" "}
            {filtered.length} records
          </span>

          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <button
              type="button"
              disabled={currentPage <= 1}
              onClick={() => setPage((value) => Math.max(1, value - 1))}
              style={{
                ...styles.select,
                minWidth: "auto",
                cursor: currentPage <= 1 ? "not-allowed" : "pointer",
                opacity: currentPage <= 1 ? 0.5 : 1,
              }}
            >
              Previous
            </button>

            <span>
              Page {currentPage} of {totalPages}
            </span>

            <button
              type="button"
              disabled={currentPage >= totalPages}
              onClick={() =>
                setPage((value) => Math.min(totalPages, value + 1))
              }
              style={{
                ...styles.select,
                minWidth: "auto",
                cursor: currentPage >= totalPages ? "not-allowed" : "pointer",
                opacity: currentPage >= totalPages ? 0.5 : 1,
              }}
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
